import { ArrayUtils } from '@shopping/common'
import { RelationProduct } from 'common/types/entities/mongo/relationProduct'
import { StateKey } from 'common/utils/flipState'
import {
  fetchRelationProduct,
  fetchRelationProductList,
  fetchRelationProductPreview,
} from 'common/services/relationProduct'
import { ProductParam, StoreCategoryParams } from 'common/types/params'
import { StoreProducts } from 'common/types/entities/store'
import { getProductByIds } from 'common/services/product'
import { RelationProductParam } from 'common/types/params/list'
import { RelationProductList } from 'common/types/entities/list'
import { Brand } from 'common/types/entities/mongo/brand'
import { WindowProduct } from 'common/types/entities/mongo/product'
import { fetchStoreCategories } from 'common/services/storeCategory'
import { StoreCategory } from 'common/types/entities/mongo/storeCategory'
import { createCacheParam, isCacheHit } from 'common/utils/reduxCache'
import { fetchBrand } from 'common/services/brandSearch'
import { ListItem, Sort, SubVertical } from 'common/constants'
import {
  fetchProductsWithZzim,
  fetchRelationProductsWithZzim,
  fetchRelationProductWithZzim,
} from 'common/services/list'
import { setProductZzim, setRelationProductZzim } from 'common/services/zzim'
import { Error } from 'common/types/entities/error'
import HttpStatus from 'common/utils/httpStatus'
import { RelationProductType } from 'common/constants/relationProduct'

export interface RelationProductState {
  relationProduct: RelationProduct
  mappingProducts: StoreProducts
  channelOtherRelationProducts: RelationProductList
  channelBestProducts: StoreProducts
  storeCategory: StoreCategory
}

export const initialState: RelationProductState = {
  relationProduct: null,
  mappingProducts: null,
  channelOtherRelationProducts: null,
  channelBestProducts: null,
  storeCategory: null,
}

const FETCH = '/relationProduct/FETCH'
const RELATION_PRODUCT_MORE = '/relationProduct/RELATION_PRODUCT_MORE'
const TOGGLE_RELATION_PRODUCT_ZZIM = '/relationProduct/TOGGLE_RELATION_PRODUCT_ZZIM'
const TOGGLE_PRODUCTS_ZZIM = '/relationProduct/TOGGLE_PRODUCTS_ZZIM'
const HIGHLIGHT_MAPPING_PRODUCT = '/relationProduct/HIGHLIGHT_MAPPING_PRODUCT'

export const getRelationProduct = (curState: StateKey, id: string, headers?: any, isPreview?: boolean) => {
  return async (dispatch, getState) => {
    const cacheParam = createCacheParam(FETCH, 'getRelationProduct', { curState, id, isPreview })
    if (isCacheHit(getState().relationProduct[curState], cacheParam)) {
      return
    }

    let relationProduct: RelationProduct = isPreview
      ? await fetchRelationProductPreview(id)
      : await fetchRelationProduct(id)
    if (!relationProduct || !relationProduct._id) {
      return
    }

    relationProduct = await fetchRelationProductWithZzim(relationProduct, headers)
    const storeCategoryParam: StoreCategoryParams = {
      categoryIds: [relationProduct.channel.storeCategoryRelations[0].storeCategory.reverse()[0]],
    }
    const channelNo = relationProduct.channelNo

    const params: ProductParam = {
      productIds: relationProduct.relationProductMappings.map(mapping => {
        return mapping.channelProductNo
      }),
      page: 1,
      pageSize: relationProduct.relationProductMappings.length,
      exposure: true,
      includeViewCount: true,
    }

    const [storeCategories, mappingProducts] = await Promise.all([
      fetchStoreCategories(storeCategoryParam),
      getProductByIds(params),
    ])

    mappingProducts.products = await fetchProductsWithZzim(mappingProducts.products, headers)

    const storeCategory = storeCategories[0]

    const relationProductParams: RelationProductParam = {
      storeId: channelNo,
      includeStoreCardInfo: true,
      subVertical: relationProduct.channel.storeCategoryRelations[0].subVertical,
      page: 1,
      pageSize: 6,
      excludeIds: [relationProduct._id],
      includeViewCount: true,
      sort: Sort.RECENT,
      relationProductType: RelationProductType.VMD,
    }
    const channelBestProductParam: ProductParam = {
      storeId: channelNo,
      best: true,
      exposure: true,
      page: 1,
      pageSize: 8,
      includeViewCount: true,
      sort: Sort.RECENT,
    }

    const [channelOtherRelationProducts, channelBestProducts] = await Promise.all([
      fetchRelationProductList(relationProductParams),
      getProductByIds(channelBestProductParam),
    ])

    channelOtherRelationProducts.relationProducts = await fetchRelationProductsWithZzim(
      channelOtherRelationProducts.relationProducts,
      headers,
    )
    channelBestProducts.products = await fetchProductsWithZzim(channelBestProducts.products, headers)

    const result: RelationProductState = {
      relationProduct,
      mappingProducts,
      channelOtherRelationProducts,
      channelBestProducts,
      storeCategory,
    }

    dispatch({
      curState,
      result,
      type: FETCH,
    })
  }
}

export const getRelationProductMore = (
  curState: StateKey,
  subvertical: SubVertical,
  channelNo: string,
  groupKey: number,
) => {
  return async (dispatch, getState) => {
    const { channelOtherRelationProducts, relationProduct } = getState().relationProduct[curState]
    const relationProductParams: RelationProductParam = {
      storeId: channelNo,
      includeStoreCardInfo: true,
      subVertical: subvertical,
      page: groupKey,
      pageSize: 6,
      excludeIds: [relationProduct._id],
    }

    const fetchChannelRelationProducts = await fetchRelationProductList(relationProductParams)
    fetchChannelRelationProducts.relationProducts = [
      ...(channelOtherRelationProducts.relationProducts || []),
      ...fetchChannelRelationProducts.relationProducts,
    ]
    dispatch({
      curState,
      channelOtherRelationProducts: fetchChannelRelationProducts,
      type: RELATION_PRODUCT_MORE,
    })
  }
}

const handleError = (responseData: any) => {
  if (!responseData.bSuccess) {
    const error: Error = {
      status: responseData.sErrorCode === 'ACCESS_DENIED' ? HttpStatus.UNAUTHORIZED : HttpStatus.OK,
      message: responseData.htReturnValue.sMessage,
    }
    throw error
  }
}

export const toggleRelationProductZzimAtVmdDetail = (curState: StateKey, productId: string, zzimed: boolean) => async (
  dispatch,
  getState,
) => {
  const responseData = await setRelationProductZzim(productId, zzimed)
  handleError(responseData)
  const state: RelationProductState = getState().relationProduct[curState]
  const relationProduct = getZzimToggledProductItem(state.relationProduct)

  dispatch({
    curState,
    relationProduct,
    type: TOGGLE_RELATION_PRODUCT_ZZIM,
  })
}

export const toggleProductsZzimAtVmdDetail = (
  curState: StateKey,
  productId: string,
  zzimed: boolean,
  listItem: ListItem,
) => async dispatch => {
  let responseData
  if (listItem === ListItem.RELATION_PRODUCT) {
    responseData = await setRelationProductZzim(productId, zzimed)
  } else {
    responseData = await setProductZzim(productId, zzimed)
  }

  handleError(responseData)

  dispatch({
    curState,
    productId,
    listItem,
    type: TOGGLE_PRODUCTS_ZZIM,
  })
}

export const highLightMappingProduct = (curState: StateKey, targetProductId: string) => async (dispatch, getState) => {
  const state = getState().relationProduct[curState]
  const mappingProducts = state.mappingProducts.products.map(product => {
    if (product._id === targetProductId) {
      return {
        ...product,
        clickedByImageCoordinate: true,
      }
    }

    return {
      ...product,
      clickedByImageCoordinate: false,
    }
  })

  dispatch({
    curState,
    mappingProducts,
    type: HIGHLIGHT_MAPPING_PRODUCT,
  })
}

const findProductItemIndex = (products: (WindowProduct | RelationProduct)[], productId: string) => {
  return products && products.findIndex(item => item._id === productId)
}

const getZzimToggledProductItem = (product: WindowProduct | RelationProduct) => {
  return {
    ...product,
    isMyZzim: !product.isMyZzim,
  }
}

const toggleState = (targetIdx: number, items: any[]) => {
  if (ArrayUtils.isEmpty(items)) {
    return []
  }

  return [...items.slice(0, targetIdx), getZzimToggledProductItem(items[targetIdx]), ...items.slice(targetIdx + 1)]
}

const relationProductReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH:
      return action.result
    case RELATION_PRODUCT_MORE:
      return {
        ...state,
        channelOtherRelationProducts: action.channelOtherRelationProducts,
      }
    case TOGGLE_RELATION_PRODUCT_ZZIM:
      return {
        ...state,
        relationProduct: action.relationProduct,
      }
    case TOGGLE_PRODUCTS_ZZIM:
      const listItem = action.listItem
      let targetItemIndex
      switch (listItem) {
        case ListItem.VMD_RELATION_WINDOW_PRODUCT:
          targetItemIndex = findProductItemIndex(state.mappingProducts.products, action.productId)
          return {
            ...state,
            mappingProducts: {
              products: toggleState(targetItemIndex, state.mappingProducts.products),
            },
          }
        case ListItem.RECOMMEND_WINDOW_PRODUCT:
          targetItemIndex = findProductItemIndex(state.channelBestProducts.products, action.productId)
          return {
            ...state,
            channelBestProducts: {
              products: toggleState(targetItemIndex, state.channelBestProducts.products),
            },
          }
        case ListItem.RELATION_PRODUCT:
          targetItemIndex = findProductItemIndex(state.channelOtherRelationProducts.relationProducts, action.productId)
          return {
            ...state,
            channelOtherRelationProducts: {
              relationProducts: toggleState(targetItemIndex, state.channelOtherRelationProducts.relationProducts),
            },
          }
      }
    case HIGHLIGHT_MAPPING_PRODUCT:
      return {
        ...state,
        mappingProducts: {
          products: action.mappingProducts,
        },
      }
    default:
      return state
  }
}

export default relationProductReducer
